import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import useInfiniteScroll from '@sorare/core/src/hooks/useInfiniteScroll';

import { MembersListRow } from 'components/rivalsUserGroup/MembersListRow';

import {
  RivalsLeagueMembersListQuery,
  RivalsLeagueMembersListQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled.div`
  min-height: 250px;
`;

type Props = {
  userGroupSlug: string;
  title?: (membersCount: Nullable<number>) => ReactNode;
};

const RIVALS_LEAGUE_MEMBERS_LIST_QUERY = gql`
  query RivalsLeagueMembersListQuery($slug: String!, $page: Int) {
    football {
      rivals {
        id
        userGroup(slug: $slug) {
          id
          slug
          affiliate
          administrator {
            slug
          }
          membershipsCount
          membershipsPaginated(pageSize: 20, page: $page) {
            currentPage
            pages
            nodes {
              id
              ...MembersListRow_userGroupMembershipInterface
            }
          }
        }
      }
    }
  }
  ${MembersListRow.fragments.userGroupMembershipInterface}
` as TypedDocumentNode<
  RivalsLeagueMembersListQuery,
  RivalsLeagueMembersListQueryVariables
>;

export const MembersList = ({ userGroupSlug, title }: Props) => {
  const { data, loadMore, loading } = usePaginatedQuery(
    RIVALS_LEAGUE_MEMBERS_LIST_QUERY,
    {
      variables: {
        slug: userGroupSlug,
      },
      connection: 'So5UserGroupMembershipsPaginated',
    }
  );

  const { membershipsPaginated, administrator, membershipsCount } =
    data?.football?.rivals?.userGroup || {};
  const { currentPage, pages } = membershipsPaginated || {};

  const { currentUser } = useCurrentUserContext();
  const isCurrentUserAdmin = currentUser?.slug === administrator?.slug;

  const { InfiniteScrollLoader } = useInfiniteScroll(
    async () =>
      loadMore(false, {
        page: typeof currentPage === 'number' ? currentPage + 1 : 0,
      }),
    (currentPage || 0) < (pages || Infinity),
    loading
  );
  return (
    <Wrapper>
      {title?.(membershipsCount)}
      {membershipsPaginated?.nodes?.map(membership => (
        <MembersListRow
          key={membership.id}
          userGroupMembership={membership}
          isCurrentUserAdmin={isCurrentUserAdmin}
          userGroupId={data?.football?.rivals?.userGroup?.id}
        />
      ))}
      <InfiniteScrollLoader />
    </Wrapper>
  );
};
